import { useState, useEffect } from "react";

const LEMON = "#D4E040";
const BLACK = "#0a0a0a";
const WHITE = "#f5f0e4";

export default function App() {
  const [loaded, setLoaded] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: -200, y: -200 });

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
  }, []);

  useEffect(() => {
    const move = (e) => setCursorPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  const fade = (delay = 0) => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? "none" : "translateY(32px)",
    transition: `opacity 1.1s ease ${delay}s, transform 1.1s ease ${delay}s`,
  });

  const services = [
    { label: "Marketing Strategy", desc: "Logical approach that cuts through noise." },
    { label: "Brand Identity", desc: "Storytelling that speaks to the right audience." },
    { label: "Growth", desc: "Authentic engagements and conversions." },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Bowlby+One&family=JetBrains+Mono:wght@300;400&display=swap');
        * { margin:0; padding:0; box-sizing:border-box; }
        ::selection { background:${LEMON}; color:${BLACK}; }
        html { scroll-behavior: smooth; }
        body { background:${BLACK}; cursor: none; }

        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.6); }
        }

        @keyframes grain {
          0%, 100% { background-position: 0% 0%; }
          25% { background-position: 50% 50%; }
          50% { background-position: 100% 0%; }
          75% { background-position: 50% 100%; }
        }

        .cursor-dot {
          position: fixed;
          width: 10px;
          height: 10px;
          background: ${LEMON};
          border-radius: 50%;
          pointer-events: none;
          z-index: 9999;
          transform: translate(-50%, -50%);
          transition: width 0.2s, height 0.2s, background 0.2s;
          mix-blend-mode: difference;
        }

        .nav-link:hover { color: ${LEMON} !important; }

        .service-row {
          border-top: 1px solid rgba(245,240,228,0.08);
          padding: 32px 0;
          display: flex;
          align-items: center;
          gap: 24;
          cursor: none;
          transition: padding-left 0.4s ease;
        }

        .service-row:hover {
          padding-left: 20px;
        }

        .hero-noise {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          opacity: 0.035;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          background-size: 256px;
          animation: grain 4s steps(1) infinite;
        }

        .lemon-dot {
          animation: pulse-dot 2.4s ease-in-out infinite;
        }
      `}</style>

      {/* Custom cursor */}
      <div
        className="cursor-dot"
        style={{ left: cursorPos.x, top: cursorPos.y }}
      />

      <div style={{ background: BLACK, color: WHITE, fontFamily: "'JetBrains Mono', monospace", minHeight: "100vh" }}>

        {/* NAV */}
        <nav style={{
          position: "fixed", top: 0, left: 0, right: 0,
          zIndex: 99, padding: "28px 48px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: `linear-gradient(to bottom, ${BLACK}cc, transparent)`,
          backdropFilter: "blur(0px)",
        }}>
          <div style={{ ...fade(0.4), display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, background: WHITE }} />
            <div className="lemon-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: LEMON }} />
            <span style={{ fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", marginLeft: 6, color: WHITE }}>
              Brick Lemon
            </span>
          </div>

          <div style={{ ...fade(0.6), display: "flex", gap: 40, alignItems: "center" }}>
            <a
              href="mailto:contact@bricklemon.com"
              style={{
                fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase",
                color: BLACK, background: LEMON,
                padding: "8px 16px", textDecoration: "none",
                transition: "background 0.3s, color 0.3s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = WHITE; e.currentTarget.style.color = BLACK; }}
              onMouseLeave={e => { e.currentTarget.style.background = LEMON; e.currentTarget.style.color = BLACK; }}
            >
              Contact
            </a>
          </div>
        </nav>

        {/* HERO */}
        <section style={{
          minHeight: "100vh", display: "flex",
          flexDirection: "column", justifyContent: "flex-end",
          padding: "0 48px 64px", position: "relative", overflow: "hidden",
        }}>
          {/* Noise overlay */}
          <div className="hero-noise" />

          {/* Vertical grid lines */}
          {[25, 50, 75].map(p => (
            <div key={p} style={{
              position: "absolute", left: `${p}%`, top: 0, bottom: 0,
              width: 1, background: "rgba(245,240,228,0.04)", pointerEvents: "none", zIndex: 0,
            }} />
          ))}

          {/* Lemon accent block */}
          <div style={{
            ...fade(0.1),
            position: "absolute", top: "18%", right: "8%",
            width: 6, height: "22vh",
            background: LEMON,
            opacity: 0.7,
          }} />

          {/* Big type */}
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{ ...fade(0.15), overflow: "hidden" }}>
              <h1 style={{
                fontFamily: "'Bowlby One', cursive",
                fontSize: "clamp(60px, 16vw, 200px)",
                lineHeight: 0.86,
                letterSpacing: "-0.01em",
                color: WHITE,
              }}>
                BRICK
              </h1>
            </div>
            <div style={{ ...fade(0.3), overflow: "hidden" }}>
              <h1 style={{
                fontFamily: "'Bowlby One', cursive",
                fontSize: "clamp(60px, 16vw, 200px)",
                lineHeight: 0.86,
                letterSpacing: "-0.01em",
                color: LEMON,
              }}>
                LEMON
              </h1>
            </div>

          </div>
        </section>

        {/* MARQUEE */}
        <div style={{ background: LEMON, overflow: "hidden", padding: "16px 0", borderTop: `1px solid ${BLACK}22` }}>
          <div style={{ display: "inline-flex", animation: "marquee 22s linear infinite", whiteSpace: "nowrap" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <>
                <span key={`a-${i}`} style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "clamp(1rem, 2.5vw, 1.8rem)",
                  letterSpacing: "0.1em",
                  color: BLACK,
                  opacity: 0.3,
                  padding: "0 64px",
                }}>
                  BRICK LEMON
                </span>
                <span key={`b-${i}`} style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "clamp(1rem, 2.5vw, 1.8rem)",
                  letterSpacing: "0.1em",
                  color: BLACK,
                  opacity: 0.3,
                  padding: "0 64px",
                }}>
                  SOLID FOUNDATION WITH FRESH IDEAS
                </span>
              </>
            ))}
          </div>
        </div>

        {/* PHILOSOPHY */}
        <section style={{ padding: "240px 48px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 64 }}>
              <div style={{ width: 24, height: 1, background: LEMON }} />
              <span style={{ fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", color: "rgba(245,240,228,0.3)" }}>
                Philosophy
              </span>
              <div style={{ width: 24, height: 1, background: LEMON }} />
            </div>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(4rem, 8vw, 7.5rem)",
              letterSpacing: "0.02em",
              color: WHITE,
              lineHeight: 0.95,
              maxWidth: 800,
              marginBottom: 40,
            }}>
              Solid Foundation<br />With <span style={{ color: LEMON }}>Fresh Ideas.</span>
            </h2>
            <p style={{
              fontSize: 15,
              letterSpacing: "0.04em",
              lineHeight: 1.9,
              color: "rgba(245,240,228,0.5)",
              maxWidth: 600,
            }}>
              Brick Lemon is a marketing studio that builds identities and crafts experiences that connect. We balance logical strategy with authentic storytelling to drive real results.
            </p>
          </div>
        </section>

        {/* SERVICES */}
        <section style={{ padding: "0 48px 140px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            {/* Section label */}
            <div style={{ marginBottom: 64, display: "flex", alignItems: "center", justifyContent: "center", gap: 20 }}>
              <div style={{ width: 24, height: 1, background: LEMON }} />
              <span style={{ fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", color: "rgba(245,240,228,0.3)" }}>
                What we do
              </span>
              <div style={{ width: 24, height: 1, background: LEMON }} />
            </div>

            {services.map((s, i) => (
              <div
                key={s.label}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  borderTop: "1px solid rgba(245,240,228,0.07)",
                  padding: "36px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 28,
                  cursor: "none",
                  transition: "padding-left 0.5s ease",
                  paddingLeft: hovered === i ? 20 : 0,
                }}
              >
                <span style={{ fontSize: 8, color: LEMON, minWidth: 28, letterSpacing: "0.1em", opacity: 0.6 }}>
                  0{i + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: "clamp(2.4rem, 5.5vw, 4.5rem)",
                    letterSpacing: "0.02em",
                    color: hovered === i ? LEMON : WHITE,
                    transition: "color 0.4s",
                    display: "block",
                    lineHeight: 1,
                  }}>
                    {s.label}
                  </span>
                  <span style={{
                    fontSize: 9, letterSpacing: "0.2em", color: "rgba(245,240,228,0.3)",
                    opacity: hovered === i ? 1 : 0,
                    transition: "opacity 0.4s",
                    display: "block",
                    marginTop: 8,
                    textTransform: "uppercase",
                  }}>
                    {s.desc}
                  </span>
                </div>
                <span style={{
                  color: LEMON,
                  opacity: hovered === i ? 1 : 0,
                  transition: "opacity 0.3s, transform 0.3s",
                  transform: hovered === i ? "translateX(0)" : "translateX(-8px)",
                  fontSize: 22,
                  flexShrink: 0,
                }}>
                  →
                </span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid rgba(245,240,228,0.07)" }} />
          </div>
        </section>

        {/* FOOTER */}
        <section style={{ background: LEMON, padding: "88px 48px 44px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(3.5rem, 10vw, 9rem)",
              lineHeight: 0.86,
              letterSpacing: "-0.01em",
              color: BLACK,
              marginBottom: 56,
            }}>
              BRICK <br /> LEMON.
            </h2>

            <div style={{
              borderTop: `1px solid ${BLACK}18`, paddingTop: 32,
            }}>
              <span style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: `${BLACK}55` }}>
                © 2026 Brick Lemon
              </span>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}